import { useTranslation } from "react-i18next";
import {
  ExpandableModule,
  GenderLabAppShell,
  GenderLabLoader,
} from "../../components";
import {
  Flex,
  Text,
  Title,
  Progress,
  Button,
  Alert,
  Anchor,
} from "@mantine/core";
import { useEffect, useState } from "react";
import { aulaService } from "../../api";
import { useNavigate, useParams } from "react-router-dom";
import { TEnrollmentProgress, TProgress } from "../../types/Aula";
import { useMediaQuery } from "@mantine/hooks";
import { IconInfoCircle } from "@tabler/icons-react";

export const EnrollmentProgressPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [enrollment, setEnrollment] = useState<TEnrollmentProgress | null>(
    null
  );
  const [isActive, setIsActive] = useState<boolean>(false);
  const isMobile = useMediaQuery("(max-width: 992px)");
  const [progress, setProgress] = useState<TProgress[]>([]);
  const minShownPercentage = 7;
  const navigate = useNavigate();

  const getEnrollmentById = async (id: string) => {
    const response = await aulaService.getEnrollmentProgressById(id);
    setEnrollment(response.data.enrollment);
    setProgress(response.data.progress);

    const endDate = new Date(response.data.enrollment.end_date);
    const currentDate = new Date(Date.now());
    const compare = currentDate <= endDate;
    setIsActive(compare);
  };

  useEffect(() => {
    if (id) {
      getEnrollmentById(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <GenderLabAppShell>
      {enrollment ? (
        <>
          <Button
            onClick={() => navigate(`/inicio`)}
            style={{
              alignSelf: "flex-start",
              marginTop: isMobile ? "3rem" : "2rem",
            }}
            variant="subtle"
          >
            Volver al Inicio
          </Button>
          <Flex direction="column" align="center" gap="sm">
            <Title order={isMobile ? 4 : 2}>{enrollment.course.name}</Title>
            <Progress.Root
              radius="md"
              mt="sm"
              w={isMobile ? "90%" : "50%"}
              size={20}
            >
              <Progress.Section
                value={
                  enrollment.percentage_completed < minShownPercentage
                    ? minShownPercentage
                    : enrollment.percentage_completed
                }
                color="blue"
              >
                <Progress.Label fz={isMobile ? "xs" : "sm"}>
                  {enrollment.percentage_completed || 0}%
                </Progress.Label>
              </Progress.Section>
            </Progress.Root>
            <Text fw={500} size="sm" c="dimmed">
              {enrollment.course.modules.length}{" "}
              {enrollment.course.modules.length === 1 ? "módulo" : "módulos"} •{" "}
              {enrollment.course.modules.reduce(
                (total, module) => total + module.lessons.length,
                0
              )}{" "}
              clases
            </Text>
            {!isActive && !enrollment.completed_at && (
              <Alert
                variant="light"
                mt="md"
                w="50%"
                color="red"
                title="No completado 😔"
                ta="justify"
                icon={<IconInfoCircle />}
              >
                Lamentablemente, no lograste completar el curso dentro del
                tiempo establecido.
              </Alert>
            )}
            {enrollment.completed_at &&
              enrollment.percentage_completed === 100 && (
                <Alert
                  variant="light"
                  mt="md"
                  w="50%"
                  color="blue"
                  title="Felicitaciones 🏆"
                  ta="justify"
                  icon={<IconInfoCircle />}
                >
                  Has completado el curso con éxito. ¡Gracias por tu esfuerzo y
                  dedicación! Recuerda que puedes descargar tu certificado{" "}
                  <Anchor
                    href={`/aula-virtual/${id}/certificado-alumno`}
                    target="_blank"
                    c="blue"
                    size="sm"
                  >
                    aquí
                  </Anchor>
                </Alert>
              )}
            {enrollment.course.modules.map((module, index) => (
              <ExpandableModule
                key={module.id}
                progress={progress}
                module={module}
                opened={module.order === enrollment.current_module}
                isEnrollmentActive={isActive}
              />
            ))}
          </Flex>
        </>
      ) : (
        <GenderLabLoader />
      )}
    </GenderLabAppShell>
  );
};
